// client/src/scheduling/utils/poolEligibility.js
// Pool eligibility for the UI only (Trello #257).
//
// ⚠️ NOT authoritative. This exists so the Job Pools page can gray out pool tabs
// you can't claim from, and so the Send-to-Pool modal can lock the pool picker on
// ETF work. Every actual claim/assign/send is validated server-side in
// api/shared/poolEligibility.js — treat that as the source of truth and keep the
// two rule sets in step. Same deliberate-port arrangement as
// api/shared/distributeHours.js ↔ utils/optimizeHours.js.
//
// Unlike the server twin this imports the shared constants rather than restating
// them, so dept normalization / leadership / schedulable definitions can only
// drift in one place (the rules below).

import {
  normalizeDept,
  jobDept,
  LEADERSHIP_LEVELS,
  isSchedulableStaff,
  JOB_TYPE_COLORS,
} from './schedConstants';

// NFP is the 990 pool. Not coded '990': a numeric-looking key jumps to the front of every
// object built from these codes.
export const POOL_CODES = ['GEN', 'EXPTAX', 'ETF', 'NFP'];

// GEN is labelled "Preparer": it replaced PE's "Preparer Pool" staff account, which is
// the name people already use. Mirrors POOL_DISPLAY in the server twin. The 990 pool
// takes the scheduler's Not For Profit job color, so it reads as the same work.
export const POOL_META = {
  GEN:    { label: 'Preparer',         blurb: 'Firm-wide small jobs',            color: '#0369A1' },
  EXPTAX: { label: 'Experienced Tax',  blurb: 'Tax-specific small jobs',         color: '#B45309' },
  ETF:    { label: 'ETF',              blurb: 'Estate / Trust / Foreign Tax',    color: '#4338CA' },
  NFP:    { label: '990',              blurb: 'Non-profit returns (990s)',       color: JOB_TYPE_COLORS['Not For Profit'].bg },
};

// Seniority rank, junior → senior. Mirrors SUGGESTION_LEVEL_OPTIONS' ordering
// (which reads senior → junior) so Manager outranks Supervisor, and Supervisor
// outranks Senior Accountant. Consultant is intentionally unranked — it counts as
// leadership for filtering, so it never needs to satisfy a minimum level.
export const LEVEL_RANK = {
  'Intern': 1,
  'Staff A': 2,
  'Staff B': 3,
  'Senior Accountant': 4,
  'Supervisor': 5,
  'Manager': 6,
  'Senior Manager': 7,
  'Principal': 8,
  'Member': 9,
};

const LEADERSHIP = new Set(LEADERSHIP_LEVELS);

// Defaults must match DEFAULT_RULES in api/shared/poolEligibility.js.
//
// ETF takes GENERAL and TAX as well as ESTATE (since 2026-10-29): of the jobs claimed
// from PE's old ETF Pool account, only 9 of 45 went to Estate staff. With that wider
// population it excludes leadership like the other pools — it only kept leadership
// while ESTATE alone (2 non-leaders) had to staff it.
// `depts` is a LIST, and an empty list means "any department" — that's what makes GEN
// firm-wide. It replaced a single `dept` string so the Exp Tax pool can take more than
// one department without a code change, and every pool's departments and minimum level
// are now editable from the Pool Audit tab (keys `<code>.depts`, `<code>.min_level`).
export const DEFAULT_RULES = {
  GEN:    { depts: [],         minLevel: null,                excludeLeadership: true,  allowNonSchedulable: false },
  EXPTAX: { depts: ['TAX'],    minLevel: 'Senior Accountant', excludeLeadership: true,  allowNonSchedulable: false },
  ETF:    { depts: ['ESTATE', 'GENERAL', 'TAX'], minLevel: null, excludeLeadership: true, allowNonSchedulable: false },
  NFP:    { depts: ['AUDIT', 'TAX', 'GENERAL'], minLevel: null, excludeLeadership: true, allowNonSchedulable: false },
};

// ── Difficulty ──────────────────────────────────────────────────────────────
// PE job complexity, mirroring the server twin. PE numbers it BACKWARDS: 1 is
// "Level 1 - Difficult", 3 is "Level 3 - Simple", 0 is "Not Selected". Show the word.
// Claim order is oldest-first within each band, with "not set" as its own band (0).
export const DIFFICULTY_META = {
  1: { label: 'Difficult', color: '#B91C1C', bg: '#FEE2E2' },
  2: { label: 'Moderate',  color: '#92400E', bg: '#FEF3C7' },
  3: { label: 'Simple',    color: '#166534', bg: '#DCFCE7' },
};
export function difficultyBand(complexity) {
  const n = Number(complexity);
  return DIFFICULTY_META[n] ? n : 0;
}
export function difficultyLabel(complexity) {
  return DIFFICULTY_META[difficultyBand(complexity)]?.label || 'Not set';
}

// Levels junior → senior for the admin min-level picklist, derived from LEVEL_RANK so
// the two can't drift. '' means no minimum.
export const LEVELS_ASCENDING = Object.keys(LEVEL_RANK).sort((a, b) => LEVEL_RANK[a] - LEVEL_RANK[b]);

export function levelRank(level) {
  return LEVEL_RANK[String(level || '').trim()] || 0;
}

// Fold rprs.PoolConfig rows over the defaults. Accepts either an array of
// { ConfigKey, ConfigValue } or a plain map.
//
// Keys are unprefixed (`exptax.min_level`, `gen.exclude_leadership`) because they
// live in a dedicated PoolConfig table. They previously sat in
// rprs.RecommendationConfig behind a 'pool.' prefix — see migrate-059.
// Parse a comma-separated department list, dropping anything unrecognised.
//
// normalizeDept maps ANY unknown string to 'OTHER', so a bare normalize would turn a
// typo into the OTHER department and quietly retarget the pool. A token only survives
// if it normalizes to something other than 'OTHER', or was literally 'OTHER'. Aliases
// still work (CONSULT → ADVISORY, ADMINGA → ADMIN). Mirrors the server twin.
export function parseDeptList(raw) {
  const out = String(raw || '').split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => ({ raw: s.toUpperCase(), norm: normalizeDept(s) }))
    .filter(x => x.norm !== 'OTHER' || x.raw === 'OTHER')
    .map(x => x.norm);
  return Array.from(new Set(out));
}

export function buildPoolRules(config) {
  const map = Array.isArray(config)
    ? config.reduce((m, r) => { m[r.ConfigKey] = r.ConfigValue; return m; }, {})
    : (config || {});

  const rules = Object.fromEntries(POOL_CODES.map(c => [c, { ...DEFAULT_RULES[c] }]));

  const bool = (raw) => {
    const v = String(raw).trim().toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
    return null;
  };

  for (const code of POOL_CODES) {
    const k = code.toLowerCase();

    const lead = bool(map[`${k}.exclude_leadership`] ?? null);
    if (lead !== null) rules[code].excludeLeadership = lead;

    const nonSched = bool(map[`${k}.allow_nonschedulable`] ?? null);
    if (nonSched !== null) rules[code].allowNonSchedulable = nonSched;

    // Per-pool minimum level. Blank clears it; unrecognised values are ignored rather
    // than silently excluding everyone.
    const rawMin = map[`${k}.min_level`];
    if (rawMin != null) {
      const min = String(rawMin).trim();
      if (!min) rules[code].minLevel = null;
      else if (LEVEL_RANK[min]) rules[code].minLevel = min;
    }

    // Departments, comma-separated. Explicitly blank = any department (how GEN stays
    // firm-wide). If every entry is unrecognised we keep the coded default, so a typo
    // can't silently widen eligibility.
    const rawDepts = map[`${k}.depts`];
    if (rawDepts != null) {
      const raw = String(rawDepts).trim();
      if (!raw) {
        rules[code].depts = [];
      } else {
        const list = parseDeptList(raw);
        if (list.length) rules[code].depts = list;
      }
    }
  }

  return rules;
}

// `staff` needs { Level, DepartmentCode, IsActive }.
export function isEligible(staff, poolCode, rules = DEFAULT_RULES) {
  const r = rules[poolCode];
  if (!r || !staff) return false;
  if (staff.IsActive === 0 || staff.IsActive === false) return false;
  if (!r.allowNonSchedulable && !isSchedulableStaff(staff.Level)) return false;
  if (r.excludeLeadership && LEADERSHIP.has(String(staff.Level || '').trim())) return false;
  // Empty depts list = any department. That's what keeps GEN firm-wide.
  const depts = Array.isArray(r.depts) ? r.depts : [];
  if (depts.length && !depts.includes(normalizeDept(staff.DepartmentCode))) return false;
  if (r.minLevel && levelRank(staff.Level) < levelRank(r.minLevel)) return false;
  return true;
}

export function eligiblePoolsForStaff(staff, rules = DEFAULT_RULES) {
  return POOL_CODES.filter(code => isEligible(staff, code, rules));
}

// Why a pool tab is disabled — shown as its tooltip so "grayed out" is never a
// mystery.
export function ineligibleReason(staff, poolCode, rules = DEFAULT_RULES) {
  const r = rules[poolCode];
  if (!r || !staff) return 'Not available';
  if (staff.IsActive === 0 || staff.IsActive === false) return 'Inactive staff';
  if (!r.allowNonSchedulable && !isSchedulableStaff(staff.Level)) {
    return `Your level (${staff.Level}) isn't scheduled on client work, so it can't claim pool jobs`;
  }
  if (r.excludeLeadership && LEADERSHIP.has(String(staff.Level || '').trim())) {
    return 'Leadership levels don\'t claim pool work';
  }
  const depts = Array.isArray(r.depts) ? r.depts : [];
  if (depts.length && !depts.includes(normalizeDept(staff.DepartmentCode))) {
    return `${POOL_META[poolCode]?.label || poolCode} is limited to ${depts.join(', ')}`;
  }
  if (r.minLevel && levelRank(staff.Level) < levelRank(r.minLevel)) {
    return `${POOL_META[poolCode]?.label || poolCode} requires ${r.minLevel} or above`;
  }
  return 'Not eligible';
}

// ── Job → pool ──────────────────────────────────────────────────────────────
// A JOB's department resolves from ServiceDesc via jobDept — the same field the
// rest of the dashboard already uses (managerJobData.js:52 sets
// `dept = SERVICE_TO_DEPT[j.ServiceDesc]`, and the sidebar's Schedule-Staff
// handoff calls `jobDept(selectedJob.ServiceDesc)`), so pool routing agrees with
// the dept filter and job coloring by construction.
//
// Worth knowing why we don't reach for rprs.Jobs.DepartmentCode instead: all
// 1,136 "Estate Trusts Foreign" jobs carry DepartmentCode 'XGEN', which
// normalizes to 'OTHER' (and 18 of them have it NULL). Keying off that column
// would stop every ETF job from auto-locking to the ETF pool.

// A 990 return of any form, by the job's PE code (990-, 990PF-, 990N-, 990T-, 990EZ-) or
// name. Not by service: 990s are filed under Business Tax as well as Not For Profit.
// Codes are usually "<client code>_<job code>" (sometimes with a trailing _1), so the
// client code before the first underscore is skipped. Mirrors is990Job in the server
// twin. `job` needs { Code, Name }.
export function is990Job(job) {
  if (!job) return false;
  const parts = String(job.Code || '').trim().split('_');
  const jobParts = parts.length > 1 ? parts.slice(1) : parts;
  return jobParts.some(p => /^990/i.test(p)) || /\b990/i.test(String(job.Name || ''));
}

// 990s are locked to the 990 pool and ETF work to the ETF pool; everything else is the
// manager's choice.
export function lockedPoolForJob(job) {
  if (is990Job(job)) return 'NFP';
  return jobDept(job && job.ServiceDesc) === 'ESTATE' ? 'ETF' : null;
}

export function allowedPoolsForJob(job) {
  const locked = lockedPoolForJob(job);
  return locked ? [locked] : ['GEN', 'EXPTAX'];
}

// Default pool selection when the Send-to-Pool modal opens.
export function defaultPoolForJob(job) {
  return lockedPoolForJob(job) || 'GEN';
}

// ── Which SERVICES may be pooled ────────────────────────────────────────────
// Mirrors DEFAULT_POOLABLE_SERVICES in api/shared/poolEligibility.js. Audit and
// Attest engagements are team-staffed, so offering Send to Pool on them is noise.
// The live list comes from rprs.PoolConfig `poolable_services` via the config read;
// this is only the fallback before that resolves (and the drift check's anchor).
//
// Keyed on ServiceDesc, not ServiceIndex — 104 jobs have ServiceIndex NULL with a
// valid ServiceDesc, so keying on the code would silently make them unpoolable.
export const DEFAULT_POOLABLE_SERVICES = [
  'Individual Tax',
  'Business Tax',
  'Not For Profit',
  'Transaction Advisory Services - Tax',
  'Estate Trusts Foreign',
];

// `services` is the config-supplied list when available, else the default.
export function isPoolableService(job, services) {
  const svc = String((job && job.ServiceDesc) || '').trim();
  if (!svc) return false;                 // no service -> don't offer the pool
  const list = Array.isArray(services) && services.length ? services : DEFAULT_POOLABLE_SERVICES;
  return list.includes(svc);
}
